import {
  DEMO_ALL_CATEGORY_STATS,
  DEMO_ALL_POSITION_DETAILS,
  DEMO_BROKERS,
  DEMO_CATEGORIES,
  DEMO_CATEGORY_MONTHLY_STATS,
  DEMO_CATEGORY_STATS_FLAT,
  DEMO_EXPENSES_BY_CATEGORY,
  DEMO_FX_RATES,
  DEMO_HOLDINGS,
  DEMO_HOLDINGS_BY_BROKER,
  DEMO_INCOME_BY_CATEGORY,
  DEMO_PORTFOLIO_HISTORY,
  DEMO_PORTFOLIO_SUMMARY,
  DEMO_POSITIONS,
  DEMO_PRICE_HISTORY,
  DEMO_RECURRING,
  DEMO_STOCK_FUNDAMENTALS,
  DEMO_STOCK_NEWS,
  DEMO_STOCK_PRICES,
  DEMO_TOP_EXPENSES,
  DEMO_TOTALS,
  DEMO_TRANSACTIONS,
  DEMO_UPCOMING_RECURRING,
} from "./demoData";

// --- Demo mode flag ---
let _isDemoMode = false;
export const isDemoMode = () => _isDemoMode;
export const enableDemoMode = () => {
  _isDemoMode = true;
};
export const disableDemoMode = () => {
  _isDemoMode = false;
};

// --- Add limits ---
const TRANSACTION_ADD_LIMIT = 3;
const HOLDING_ADD_LIMIT = 2;

// --- Event bus for limit dialog ---
const _limitListeners = new Set();
export const onLimitReached = (fn) => {
  _limitListeners.add(fn);
  return () => _limitListeners.delete(fn);
};
const emitLimitReached = (type) => _limitListeners.forEach((fn) => fn(type));

// --- Mutable in-memory state ---
let state = {
  transactions: [...DEMO_TRANSACTIONS],
  categories: [...DEMO_CATEGORIES],
  recurring: [...DEMO_RECURRING],
  holdings: [...DEMO_HOLDINGS],
  brokers: [...DEMO_BROKERS],
  addedTransactions: 0,
  addedHoldings: 0,
  nextId: 1000,
};

export const resetDemoState = () => {
  state = {
    transactions: [...DEMO_TRANSACTIONS],
    categories: [...DEMO_CATEGORIES],
    recurring: [...DEMO_RECURRING],
    holdings: [...DEMO_HOLDINGS],
    brokers: [...DEMO_BROKERS],
    addedTransactions: 0,
    addedHoldings: 0,
    nextId: 1000,
  };
};

// --- Transaction mocks ---
export const demoGetTransactions = (type = "") => {
  if (!type) return Promise.resolve([...state.transactions]);
  const filtered = state.transactions.filter((t) => t.type === type);
  return Promise.resolve(filtered);
};

export const demoCreateTransaction = (transaction) => {
  if (state.addedTransactions >= TRANSACTION_ADD_LIMIT) {
    emitLimitReached("transaction");
    return Promise.resolve(null);
  }
  state.addedTransactions++;
  const category =
    state.categories.find(
      (c) =>
        c.id === transaction.category_id || c.id === transaction.categoryid,
    ) || {};
  const newTx = {
    id: `d-added-${state.nextId++}`,
    date: transaction.date || new Date().toISOString().split("T")[0],
    amount: parseFloat(transaction.amount),
    currency: transaction.currency || "USD",
    vendor: transaction.vendor || "",
    note: transaction.note || "",
    category_id: transaction.category_id || transaction.categoryid,
    category_name: category.name || "",
    type: transaction.type || "expense",
    context_type: "personal",
    group_id: null,
  };
  state.transactions.unshift(newTx);
  return Promise.resolve({ data: newTx, status: 201 });
};

export const demoDeleteTransaction = (id) => {
  state.transactions = state.transactions.filter(
    (t) => String(t.id) !== String(id),
  );
  return Promise.resolve({ success: true });
};

export const demoGetTransactionById = (id) => {
  const tx = state.transactions.find((t) => String(t.id) === String(id));
  return Promise.resolve(tx || null);
};

export const demoGetTransactionByIdFlexible = (id) => {
  const tx = state.transactions.find((t) => String(t.id) === String(id));
  if (!tx) return Promise.reject(new Error("Transaction not found"));
  return Promise.resolve({
    ...tx,
    found_in_context: "personal",
    group_name: null,
  });
};

export const demoUpdateTransaction = (id, updatedData) => {
  const idx = state.transactions.findIndex((t) => String(t.id) === String(id));
  if (idx !== -1) {
    state.transactions[idx] = { ...state.transactions[idx], ...updatedData };
  }
  return Promise.resolve({
    status: 200,
    data: state.transactions[idx] || updatedData,
  });
};

// --- Recurring mocks ---
export const demoGetRecurring = (type = "") => {
  if (!type) return Promise.resolve([...state.recurring]);
  return Promise.resolve(
    state.recurring.filter((r) => r.type === type || !type),
  );
};

export const demoGetRecurringByIdFlexible = (id) => {
  const rec = state.recurring.find((r) => String(r.id) === String(id));
  if (!rec) return Promise.reject(new Error("Recurring transaction not found"));
  return Promise.resolve({
    ...rec,
    found_in_context: "personal",
    group_name: null,
  });
};

export const demoUpdateRecurring = (id, updatedData) => {
  const idx = state.recurring.findIndex((r) => String(r.id) === String(id));
  if (idx !== -1) {
    state.recurring[idx] = { ...state.recurring[idx], ...updatedData };
  }
  return Promise.resolve({
    status: 200,
    data: state.recurring[idx] || updatedData,
  });
};

export const demoGetUpcomingRecurring = () => {
  const totalDue = DEMO_UPCOMING_RECURRING.reduce(
    (sum, r) => sum + r.amount,
    0,
  );
  const byCategory = {};
  DEMO_UPCOMING_RECURRING.forEach((r) => {
    if (!byCategory[r.category_name])
      byCategory[r.category_name] = {
        category: r.category_name,
        amount: 0,
        count: 0,
      };
    byCategory[r.category_name].amount += r.amount;
    byCategory[r.category_name].count += 1;
  });
  return Promise.resolve({
    months: 1,
    totalDue: parseFloat(totalDue.toFixed(2)),
    categoryBreakdown: Object.values(byCategory),
  });
};

// --- Category mocks ---
export const demoGetCategories = () => Promise.resolve([...state.categories]);

export const demoGetCategoryById = (id) => {
  const cat = state.categories.find((c) => String(c.id) === String(id));
  return Promise.resolve(cat || null);
};

export const demoGetCategoryByName = (name) => {
  const cat = state.categories.find(
    (c) => c.name.toLowerCase() === name.toLowerCase(),
  );
  return Promise.resolve(cat || null);
};

export const demoAddCategory = () => {
  emitLimitReached("category");
  return Promise.resolve(null);
};

// --- Stats mocks ---
export const demoGetTotals = () => Promise.resolve({ ...DEMO_TOTALS });

// Returns { income: [...monthly], expenses: [...monthly], investments: [...monthly], savings: [...monthly] }
export const demoGetAllCategoryStats = () =>
  Promise.resolve({ ...DEMO_ALL_CATEGORY_STATS });

export const demoGetExpensesByCategory = () =>
  Promise.resolve([...DEMO_EXPENSES_BY_CATEGORY]);
export const demoGetIncomeByCategory = () =>
  Promise.resolve([...DEMO_INCOME_BY_CATEGORY]);
export const demoGetTopExpenses = () => {
  const totalExpenses = DEMO_EXPENSES_BY_CATEGORY.reduce(
    (sum, e) => sum + e.total,
    0,
  );
  const topExpenses = [...DEMO_EXPENSES_BY_CATEGORY]
    .sort((a, b) => b.total - a.total)
    .slice(0, 5)
    .map((e) => ({
      category: e.category,
      amount: e.total,
      transactionCount: e.count,
      percentage: parseFloat(((e.total / totalExpenses) * 100).toFixed(1)),
    }));
  return Promise.resolve({
    topExpenses,
    totalExpenses: parseFloat(totalExpenses.toFixed(2)),
  });
};

// Returns { category: [...monthly breakdown for selected category] }
export const demoGetCategoryStats = (_, categoryId) => {
  const data = DEMO_CATEGORY_MONTHLY_STATS[categoryId] || [];
  return Promise.resolve({ category: data });
};

// Returns flat list (used by filter chips — needs id, name)
export const demoGetCategoriesWithGroups = () =>
  Promise.resolve([...DEMO_CATEGORY_STATS_FLAT]);

// Returns full category list with display_name and context_name for dropdowns
export const demoGetAllCategoriesWithGroups = () =>
  Promise.resolve(
    state.categories.map((cat) => ({
      ...cat,
      display_name: cat.name,
      context_name: "Personal",
    })),
  );

export const demoGetExpensesByVendor = () =>
  Promise.resolve([...DEMO_TOP_EXPENSES]);
export const demoGetInvestmentsBySource = () =>
  Promise.resolve([
    { vendor: "Interactive Brokers", total: 4037.5, currency: "USD" },
    { vendor: "Robinhood", total: 1750.0, currency: "USD" },
  ]);

// --- Portfolio mocks ---
export const demoGetBrokers = () => Promise.resolve([...state.brokers]);

export const demoGetHoldings = (brokerId = null) => {
  if (brokerId)
    return Promise.resolve(
      state.holdings.filter((h) => h.broker_id === brokerId),
    );
  return Promise.resolve([...state.holdings]);
};

export const demoCreateHolding = (holdingData) => {
  if (state.addedHoldings >= HOLDING_ADD_LIMIT) {
    emitLimitReached("holding");
    return Promise.resolve(null);
  }
  state.addedHoldings++;
  const broker =
    state.brokers.find((b) => b.id === holdingData.broker_id) || {};
  const newHolding = {
    id: state.nextId++,
    ticker_symbol: holdingData.ticker_symbol,
    company_name: holdingData.company_name || "",
    transaction_type: holdingData.transaction_type || "BUY",
    quantity: holdingData.quantity,
    purchase_price: holdingData.purchase_price,
    total_cost: holdingData.quantity * holdingData.purchase_price,
    purchase_date:
      holdingData.purchase_date || new Date().toISOString().split("T")[0],
    currency: holdingData.currency || "USD",
    broker_id: holdingData.broker_id,
    broker_name: broker.name || "",
    exchange: holdingData.exchange || "",
    fees: holdingData.fees || 0,
    notes: holdingData.notes || "",
  };
  state.holdings.unshift(newHolding);
  return Promise.resolve(newHolding);
};

export const demoDeleteHolding = (id) => {
  state.holdings = state.holdings.filter((h) => h.id !== id);
  return Promise.resolve({ success: true });
};

export const demoGetPositions = () => Promise.resolve([...DEMO_POSITIONS]);

export const demoGetStockPrices = (tickers) => {
  const tickerArr = Array.isArray(tickers)
    ? tickers
    : tickers.split(",").map((t) => t.trim());
  const result = tickerArr.map(
    (t) =>
      DEMO_STOCK_PRICES[t] || {
        ticker_symbol: t,
        current_price: 100.0,
        price_change: 0,
        price_change_percent: 0,
        fifty_two_week_high: 120,
        fifty_two_week_low: 80,
        currency: "USD",
      },
  );
  return Promise.resolve(result);
};

export const demoRefreshPricesBatch = (tickers) => demoGetStockPrices(tickers);

export const demoGetPortfolioSummary = () =>
  Promise.resolve({
    ...DEMO_PORTFOLIO_SUMMARY,
    positions: [...DEMO_POSITIONS],
  });
export const demoGetPortfolioHistory = () =>
  Promise.resolve([...DEMO_PORTFOLIO_HISTORY]);
export const demoGetHoldingsByBroker = () =>
  Promise.resolve([...DEMO_HOLDINGS_BY_BROKER]);
export const demoGetAllPositionDetails = () =>
  Promise.resolve({ ...DEMO_ALL_POSITION_DETAILS });
export const demoGetFXRates = () =>
  Promise.resolve({ rates: { ...DEMO_FX_RATES } });
export const demoGetUserTickers = () =>
  Promise.resolve(["AAPL", "NVDA", "MSFT"]);
export const demoGetStockTickers = () =>
  Promise.resolve([
    "AAPL",
    "NVDA",
    "MSFT",
    "TSLA",
    "AMZN",
    "GOOGL",
    "META",
    "BRK.B",
  ]);
export const demoGetStockFundamentals = (ticker) =>
  Promise.resolve(DEMO_STOCK_FUNDAMENTALS[ticker] || null);
export const demoGetStockPriceHistory = (ticker) =>
  Promise.resolve(DEMO_PRICE_HISTORY[ticker] || []);
export const demoGetStockNews = (ticker) =>
  Promise.resolve(DEMO_STOCK_NEWS[ticker] || []);
export const demoGetStockPrice = (ticker) =>
  Promise.resolve(
    DEMO_STOCK_PRICES[ticker] || {
      ticker_symbol: ticker,
      current_price: 100.0,
      price_change: 0,
      price_change_percent: 0,
      currency: "USD",
    },
  );

// --- Profile mocks ---
export const demoGetProfile = () =>
  Promise.resolve([
    {
      id: 1,
      user_id: "demo-user-001",
      preferred_currency: "USD",
      email: "demo@organizeflow.app",
      full_name: "Demo User",
      created_at: "2026-01-01T00:00:00.000Z",
    },
  ]);

// --- Invitation mocks (always empty in demo) ---
export const demoGetPendingInvitations = () => Promise.resolve([]);
